// pages/PrivacyPolicyPage.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { COLORS } from '../constants/colors';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 16px;
  color: #333;
`;

const LastUpdated = styled.p`
  color: #868e96;
  margin-bottom: 32px;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 16px;
  color: #495057;
  margin-top: 24px;
`;

const SubTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 12px;
  color: #495057;
  margin-top: 16px;
`;

const Text = styled.p`
  line-height: 1.8;
  color: #666;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 12px 0;
  padding-left: 24px;
`;

const ListItem = styled.li`
  line-height: 1.8;
  color: #666;
  margin-bottom: 8px;
`;

const EmailLink = styled.a`
  color: ${COLORS.PRIMARY};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const HighlightBox = styled.div`
  background: #f8f9fa;
  border-left: 4px solid ${COLORS.PRIMARY};
  padding: 16px 20px;
  margin: 20px 0;
`;

export const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <Container>
      <Title>{t('privacy.title')}</Title>
      <LastUpdated>{t('privacy.lastUpdated')}: {new Date().toLocaleDateString()}</LastUpdated>

      <Section>
        <Text>{t('privacy.introduction')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataController.title')}</SectionTitle>
        <Text>
          <strong>{t('privacy.dataController.company')}:</strong> CC VALUE SRL<br />
          <strong>{t('privacy.dataController.address')}:</strong> sat Valea Seaca, comuna Baltatesti, strada Vadului, nr. 102A, judet Neamt, Romania<br />
          <strong>{t('privacy.dataController.email')}:</strong> <EmailLink href="mailto:contact@storify-now.com">contact@storify-now.com</EmailLink>
        </Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataCollection.title')}</SectionTitle>
        
        <SubTitle>{t('privacy.dataCollection.accountInfo.title')}</SubTitle>
        <Text>{t('privacy.dataCollection.accountInfo.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataCollection.accountInfo.items.email')}</ListItem>
          <ListItem>{t('privacy.dataCollection.accountInfo.items.username')}</ListItem>
          <ListItem>{t('privacy.dataCollection.accountInfo.items.password')}</ListItem>
          <ListItem>{t('privacy.dataCollection.accountInfo.items.ageConsent')}</ListItem>
        </List>

        <SubTitle>{t('privacy.dataCollection.googleLogin.title')}</SubTitle>
        <Text>{t('privacy.dataCollection.googleLogin.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataCollection.googleLogin.items.email')}</ListItem>
          <ListItem>{t('privacy.dataCollection.googleLogin.items.name')}</ListItem>
          <ListItem>{t('privacy.dataCollection.googleLogin.items.profilePicture')}</ListItem>
        </List>

        <SubTitle>{t('privacy.dataCollection.usage.title')}</SubTitle>
        <Text>{t('privacy.dataCollection.usage.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataCollection.usage.items.stories')}</ListItem>
          <ListItem>{t('privacy.dataCollection.usage.items.preferences')}</ListItem>
          <ListItem>{t('privacy.dataCollection.usage.items.interactions')}</ListItem>
        </List>

        <SubTitle>{t('privacy.dataCollection.technical.title')}</SubTitle>
        <Text>{t('privacy.dataCollection.technical.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataCollection.technical.items.ip')}</ListItem>
          <ListItem>{t('privacy.dataCollection.technical.items.browser')}</ListItem>
          <ListItem>{t('privacy.dataCollection.technical.items.device')}</ListItem>
          <ListItem>{t('privacy.dataCollection.technical.items.cookies')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataUsage.title')}</SectionTitle>
        <Text>{t('privacy.dataUsage.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataUsage.items.service')}</ListItem>
          <ListItem>{t('privacy.dataUsage.items.personalization')}</ListItem>
          <ListItem>{t('privacy.dataUsage.items.communication')}</ListItem>
          <ListItem>{t('privacy.dataUsage.items.improvement')}</ListItem>
          <ListItem>{t('privacy.dataUsage.items.security')}</ListItem>
          <ListItem>{t('privacy.dataUsage.items.legal')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.legalBasis.title')}</SectionTitle>
        <Text>{t('privacy.legalBasis.description')}</Text>
        <List>
          <ListItem><strong>{t('privacy.legalBasis.items.consent.title')}:</strong> {t('privacy.legalBasis.items.consent.description')}</ListItem>
          <ListItem><strong>{t('privacy.legalBasis.items.contract.title')}:</strong> {t('privacy.legalBasis.items.contract.description')}</ListItem>
          <ListItem><strong>{t('privacy.legalBasis.items.legitimate.title')}:</strong> {t('privacy.legalBasis.items.legitimate.description')}</ListItem>
          <ListItem><strong>{t('privacy.legalBasis.items.legal.title')}:</strong> {t('privacy.legalBasis.items.legal.description')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataSharing.title')}</SectionTitle>
        <Text>{t('privacy.dataSharing.description')}</Text>
        <List>
          <ListItem><strong>{t('privacy.dataSharing.items.serviceProviders.title')}:</strong> {t('privacy.dataSharing.items.serviceProviders.description')}</ListItem>
          <ListItem><strong>{t('privacy.dataSharing.items.google.title')}:</strong> {t('privacy.dataSharing.items.google.description')}</ListItem>
          <ListItem><strong>{t('privacy.dataSharing.items.legal.title')}:</strong> {t('privacy.dataSharing.items.legal.description')}</ListItem>
        </List>
        <HighlightBox>
          <Text style={{ marginBottom: 0 }}>{t('privacy.dataSharing.noSelling')}</Text>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataSecurity.title')}</SectionTitle>
        <Text>{t('privacy.dataSecurity.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataSecurity.items.encryption')}</ListItem>
          <ListItem>{t('privacy.dataSecurity.items.passwords')}</ListItem>
          <ListItem>{t('privacy.dataSecurity.items.access')}</ListItem>
          <ListItem>{t('privacy.dataSecurity.items.monitoring')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.dataRetention.title')}</SectionTitle>
        <Text>{t('privacy.dataRetention.description')}</Text>
        <List>
          <ListItem>{t('privacy.dataRetention.items.active')}</ListItem>
          <ListItem>{t('privacy.dataRetention.items.inactive')}</ListItem>
          <ListItem>{t('privacy.dataRetention.items.deletion')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.yourRights.title')}</SectionTitle>
        <Text>{t('privacy.yourRights.description')}</Text>
        <List>
          <ListItem><strong>{t('privacy.yourRights.items.access.title')}:</strong> {t('privacy.yourRights.items.access.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.rectification.title')}:</strong> {t('privacy.yourRights.items.rectification.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.erasure.title')}:</strong> {t('privacy.yourRights.items.erasure.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.restriction.title')}:</strong> {t('privacy.yourRights.items.restriction.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.portability.title')}:</strong> {t('privacy.yourRights.items.portability.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.objection.title')}:</strong> {t('privacy.yourRights.items.objection.description')}</ListItem>
          <ListItem><strong>{t('privacy.yourRights.items.withdraw.title')}:</strong> {t('privacy.yourRights.items.withdraw.description')}</ListItem>
        </List>
        <HighlightBox>
          <Text style={{ marginBottom: 0 }}>
            {t('privacy.yourRights.exercise')}: <EmailLink href="mailto:contact@storify-now.com">contact@storify-now.com</EmailLink>
          </Text>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.cookies.title')}</SectionTitle>
        <Text>{t('privacy.cookies.description')}</Text>
        <List>
          <ListItem><strong>{t('privacy.cookies.items.essential.title')}:</strong> {t('privacy.cookies.items.essential.description')}</ListItem>
          <ListItem><strong>{t('privacy.cookies.items.functional.title')}:</strong> {t('privacy.cookies.items.functional.description')}</ListItem>
          <ListItem><strong>{t('privacy.cookies.items.analytics.title')}:</strong> {t('privacy.cookies.items.analytics.description')}</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.thirdParty.title')}</SectionTitle>
        <SubTitle>Google OAuth</SubTitle>
        <Text>{t('privacy.thirdParty.google')}</Text>
        <Text>
          <EmailLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            {t('privacy.thirdParty.googleLink')}
          </EmailLink>
        </Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.children.title')}</SectionTitle>
        <Text>{t('privacy.children.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.international.title')}</SectionTitle>
        <Text>{t('privacy.international.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.changes.title')}</SectionTitle>
        <Text>{t('privacy.changes.description')}</Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.contact.title')}</SectionTitle>
        <Text>{t('privacy.contact.description')}</Text>
        <Text>
          <strong>{t('privacy.contact.email')}:</strong> <EmailLink href="mailto:contact@storify-now.com">contact@storify-now.com</EmailLink><br />
          <strong>{t('privacy.contact.dpo')}:</strong> <EmailLink href="mailto:contact@storify-now.com">contact@storify-now.com</EmailLink>
        </Text>
      </Section>

      <Section>
        <SectionTitle>{t('privacy.supervisory.title')}</SectionTitle>
        <Text>{t('privacy.supervisory.description')}</Text>
      </Section>
    </Container>
  );
};